import { toMoney } from "shared/utils";
import { Warning } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/common";
import {
  PaystubDocumentPaystub,
  PaystubInscribeAdditionalData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/paystub";
import { DocumentCard } from "views/applications-id-report/slices/IncomeReportSlice/components/DocumentCard";
import { SimpleTable } from "../components/SimpleTable";
import { EmployeeInformationSlice } from "./EmployeeInformationSlice";
import { EmployerInformationSlice } from "./EmployerInformationSlice";
import { PaystubDetailsSlice } from "./PaystubDetailsSlice";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { parseRiskLevel } from "views/applications-id-report/utils/parseRiskLevel";
import { WarningRecommendation } from "../../WarningRecommendation";
import { NameMismatchAlert } from "../../NameMismatchAlert";
import { Show } from "shared/components";
import { DocumentVerificationProvider, IncomeStatus } from "shared/types";
import { ReasonCode } from "types/landlord/reports";
import { getPaystubReasonCodes } from "../utils";

type InscribeRowData = {
  sourceStatus?: IncomeStatus;
  additional_data?: PaystubInscribeAdditionalData;
  trust_score?: number;
};

type ExpandedRowData = (PaystubDocumentPaystub | Warning) & InscribeRowData;

export const ExpandedTableRow = ({
  rowData,
  provider
}: {
  rowData: ExpandedRowData;
  provider: DocumentVerificationProvider;
}) => {
  const isInscribe = provider === DocumentVerificationProvider.Inscribe;
  const isWarningEntity = "doc_info" in rowData;
  const nameMismatch = isWarningEntity ? null : rowData.name_mismatch;
  const hasAsset = Boolean(rowData?.asset?.asset);

  // Provider-specific fields
  let status: "Rejected" | "Warning" | "Accepted" | undefined;
  let docScore: number;
  let reasonCodes: ReasonCode[];
  let rejectionReason: string | undefined;
  const inscribeRiskLevel = isInscribe
    ? rowData?.additional_data?.risk_level
    : undefined;

  if (isInscribe) {
    docScore = rowData?.trust_score ?? 0;
    status = parseRiskLevel(inscribeRiskLevel);
    reasonCodes = getPaystubReasonCodes(rowData?.additional_data);
    rejectionReason = reasonCodes[0]?.description;
  } else {
    const formAnalysis = isWarningEntity
      ? rowData?.form_analysis
      : rowData?.doc_analysis?.form_analysis;
    docScore = formAnalysis?.[0]?.form_authenticity?.score ?? 0;
    reasonCodes = formAnalysis?.[0]?.form_authenticity?.reason_codes ?? [];
    status = parseDocStatus(docScore)!;
    rejectionReason = isWarningEntity
      ? (rowData?.doc_info?.rejection_reason ?? reasonCodes[0]?.description)
      : reasonCodes[0]?.description;
  }

  return (
    <>
      {nameMismatch ? (
        <NameMismatchAlert nameMismatch={nameMismatch} className="mb-4" />
      ) : null}

      <WarningRecommendation
        status={status}
        riskLevel={inscribeRiskLevel}
        rejectionReason={rejectionReason}
        score={docScore}
        additionalData={{ filename: rowData?.asset?.asset?.filename }}
      />

      <Show if={hasAsset}>
        <div className="h-[1px] w-full bg-alpha-40 my-3" />
        <DocumentCard
          score={docScore}
          asset={rowData?.asset?.asset}
          reasonCodes={reasonCodes}
          riskLevel={inscribeRiskLevel}
          riskAssessmentSummary={
            isInscribe
              ? rowData?.additional_data?.risk_assessment_summary
              : undefined
          }
          ignorePrint
        />
      </Show>

      {isWarningEntity ? null : (
        <>
          <PaystubDetailsSlice paystub={rowData} />
          <EmployerInformationSlice paystub={rowData} />
          <EmployeeInformationSlice paystub={rowData} />

          {/* Pay distribution table (currently Ocrolus only - Inscribe does not yet include this data) */}
          <Show if={Boolean(rowData?.pay_distribution?.length)}>
            <SimpleTable
              data={rowData?.pay_distribution ?? []}
              columns={[
                {
                  label: "Bank Name",
                  render: (d) => String(d.bank_name) || "-"
                },
                {
                  label: "Current pay",
                  render: (d) => toMoney((d.current_pay?.cents || 0) / 100)
                },
                {
                  label: "Account",
                  render: (d) => String(d.account_number) || "-"
                },
                {
                  label: "Type",
                  render: (d) => d.bank_account_type || "-"
                },
                {
                  label: "Description",
                  render: (d) => d.description || "-"
                }
              ]}
            />
          </Show>

          <div className="h-px w-full bg-alpha-100" />

          {/* Earnings table | Earning | Current pay | YTD pay | Cur. hrs | Description */}
          <Show if={Boolean(rowData?.earnings?.totals?.length)}>
            <SimpleTable
              data={rowData?.earnings?.totals ?? []}
              columns={[
                {
                  label: "Earning",
                  render: (e) => e.description || "-"
                },
                {
                  label: "Current pay",
                  render: (e) =>
                    !e.current_pay ? "-" : toMoney(e.current_pay.cents / 100)
                },
                {
                  label: "YTD pay",
                  render: (e) =>
                    !e.ytd_pay ? "-" : toMoney(e.ytd_pay.cents / 100)
                },
                {
                  label: "Current hours",
                  render: (e) => e.current_hours || "-"
                },
                {
                  label: "Description",
                  render: (e) => e.canonical_description || "-"
                }
              ]}
            />
          </Show>

          {/* Deduction table | Deduction | Current pay */}
          <Show if={Boolean(rowData?.deductions?.totals?.length)}>
            <SimpleTable
              data={rowData?.deductions?.totals ?? []}
              columns={[
                {
                  label: "Deduction",
                  render: (d) => d.description?.replaceAll("_", " ") || "-",
                  className: "w-1/5"
                },
                {
                  label: "Current pay",
                  render: (d) =>
                    !d.current_pay ? "-" : toMoney(d.current_pay.cents / 100)
                }
              ]}
            />
          </Show>
        </>
      )}
    </>
  );
};
